export type AppEnv = 'production' | 'staging' | 'development' | 'development2' | 'qa' | 'local'

export const APP_ENV: AppEnv = (process.env.NEXT_PUBLIC_APP_ENV || 'local') as AppEnv

export const IS_PRODUCTION = APP_ENV === 'production'
export const IS_STAGING = APP_ENV === 'staging'

// QA は DEV のコピーのため development と同じ扱い（API設定の動的変更を許可）
export const isApiConfigEnabled = APP_ENV === 'development' || APP_ENV === 'qa' || APP_ENV === 'local'

const STORAGE_KEYS = {
  BACKEND_API_URL: 'dev_backend_api_url',
} as const

export interface ApiConfig {
  backendApiUrl: string
}

// 環境ごとのデフォルトURL定義
const DEFAULT_URLS: Record<AppEnv, { backend: string }> = {
  production: {
    backend: 'https://pf-api.toridori.me',
  },
  staging: {
    backend: 'https://pf-api.stg.toridori.me',
  },
  development: {
    backend: 'https://toridori-marketing-api.xdev.toridori.me',
  },
  development2: {
    backend: 'https://toridori-marketing-api-2.xdev.toridori.me',
  },
  // QA環境（= DEV環境のコピー / 独立VPC）。向き先は *.qa.toridori.me。画像CDNは dev 共用のため image は xdev を継承する。
  qa: {
    backend: 'https://toridori-marketing-api.qa.toridori.me',
  },
  local: {
    backend: process.env.NEXT_PUBLIC_BACKEND_API_HOST || 'http://localhost:3031',
  },
}

// API設定の戦略インターフェース
export interface ApiConfigStrategy {
  saveConfig(config: Partial<ApiConfig>): void
  loadConfig(): ApiConfig
  resetConfig(): void
  getUrls(): { backend: string }
}

// 本番環境用の実装（静的な設定のみ）
class ProductionApiConfigStrategy implements ApiConfigStrategy {
  private readonly config: ApiConfig

  constructor() {
    const urls = DEFAULT_URLS[APP_ENV]
    this.config = {
      backendApiUrl: urls.backend,
    }
  }

  saveConfig(): void {
    // 本番環境では設定の保存は行わない
  }

  loadConfig(): ApiConfig {
    return this.config
  }

  resetConfig(): void {
    // 本番環境ではリセットは行わない
  }

  getUrls(): { backend: string } {
    return {
      backend: this.config.backendApiUrl,
    }
  }
}

// 開発環境用の実装（動的な設定変更が可能）
class DevelopmentApiConfigStrategy implements ApiConfigStrategy {
  private getDefaultConfig(): ApiConfig {
    const urls = DEFAULT_URLS[APP_ENV]
    return {
      backendApiUrl: urls.backend,
    }
  }

  saveConfig(config: Partial<ApiConfig>): void {
    if (typeof window === 'undefined') return

    const currentConfig = this.loadConfig()
    const updatedConfig = { ...currentConfig, ...config }

    localStorage.setItem(STORAGE_KEYS.BACKEND_API_URL, updatedConfig.backendApiUrl)
  }

  loadConfig(): ApiConfig {
    const defaultConfig = this.getDefaultConfig()

    if (typeof window === 'undefined') {
      return defaultConfig
    }

    return {
      backendApiUrl: localStorage.getItem(STORAGE_KEYS.BACKEND_API_URL) || defaultConfig.backendApiUrl,
    }
  }

  resetConfig(): void {
    if (typeof window === 'undefined') return

    localStorage.removeItem(STORAGE_KEYS.BACKEND_API_URL)
  }

  getUrls(): { backend: string } {
    const config = this.loadConfig()
    return {
      backend: config.backendApiUrl,
    }
  }
}

// 環境に応じた戦略を選択してエクスポート
export const apiConfigStrategy: ApiConfigStrategy = isApiConfigEnabled
  ? new DevelopmentApiConfigStrategy()
  : new ProductionApiConfigStrategy()

// 現在のAPI URLを取得する関数のみエクスポート
export function getCurrentApiUrls(): { backend: string } {
  return apiConfigStrategy.getUrls()
}

export function validateUrl(url: string, allowHttp: boolean = false): boolean {
  try {
    const parsed = new URL(url)
    if (!allowHttp && parsed.protocol !== 'https:') {
      return APP_ENV === 'local'
    }
    return true
  } catch {
    return false
  }
}
